import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { ArrowDownLeft, ArrowUpRight, MoveLeft, MoveRight, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useOcppMessages, type Charger, type OcppMessage } from '../../api/chargers';
import { DataTable } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { colours } from '../../theme';

const time = new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
const mono = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/**
 * One OCPP exchange: a request (Call) and the reply to it (CallResult, or CallError).
 * OCPP sends them as separate messages that share a message ID; the reply doesn't name
 * the action, so they're paired here and shown together.
 */
interface Exchange {
  id: string;
  action: string;
  /** Who asked: 'in' = the charger asked Pulsepoint, 'out' = Pulsepoint asked the charger. */
  direction: 'in' | 'out';
  request: OcppMessage | null;
  reply: OcppMessage | null;
  at: string;
}

/** Pairs each request with its reply. Messages arrive newest first; exchanges keep that order. */
export function pairMessages(messages: OcppMessage[]): Exchange[] {
  const byKey = new Map<string, Exchange>();
  const out: Exchange[] = [];
  for (const m of messages) {
    const isRequest = m.messageTypeId === 2;
    // A reply travels the other way from its request.
    const askedBy = isRequest ? m.direction : m.direction === 'in' ? 'out' : 'in';
    const key = `${askedBy}|${m.messageId}`;
    let ex = byKey.get(key);
    if (!ex || (isRequest ? ex.request : ex.reply)) {
      ex = { id: m.id, action: m.action ?? '(unknown)', direction: askedBy, request: null, reply: null, at: m.createdAt };
      byKey.set(key, ex);
      out.push(ex);
    }
    if (isRequest) {
      ex.request = m;
      ex.at = m.createdAt;
      ex.action = m.action ?? ex.action;
    } else {
      ex.reply = m;
      if (!ex.action || ex.action === '(unknown)') ex.action = m.action ?? '(unknown)';
    }
  }
  return out;
}

/** What the reply says, in a word: Accepted, Rejected, an error, or nothing yet. */
function outcome(ex: Exchange): { text: string; tone: 'ok' | 'bad' | 'none' } | null {
  if (!ex.reply) {
    if (!ex.request) return null;
    const waited = Date.now() - new Date(ex.request.createdAt).getTime();
    return { text: waited > 60_000 ? 'No reply' : 'Waiting for a reply', tone: waited > 60_000 ? 'bad' : 'none' };
  }
  if (ex.reply.messageTypeId === 4) return { text: ex.reply.errorCode ?? 'Error', tone: 'bad' };
  const p = ex.reply.payload as { status?: string; idTagInfo?: { status?: string } } | null;
  const status = p?.status ?? p?.idTagInfo?.status;
  if (!status) return null;
  return { text: status, tone: /^(Accepted|Unlocked)$/.test(status) ? 'ok' : 'bad' };
}

function replyTime(ex: Exchange): string {
  if (!ex.request || !ex.reply) return '';
  const ms = new Date(ex.reply.createdAt).getTime() - new Date(ex.request.createdAt).getTime();
  return ms < 1000 ? `${Math.max(ms, 0)} ms` : `${(ms / 1000).toFixed(1)} s`;
}

const body = (m: OcppMessage, pretty: boolean) =>
  m.messageTypeId === 4 ? `${m.errorCode}: ${m.errorDescription ?? ''}` : JSON.stringify(m.payload, null, pretty ? 2 : undefined);

/** The raw OCPP traffic with this charger (admins): each request with its reply, newest first. */
export function MessagesTab({ charger }: { charger: Charger }) {
  const [hideHeartbeats, setHide] = useState(true);
  const messages = useOcppMessages(charger.id, hideHeartbeats);
  const [open, setOpen] = useState<string | null>(null);
  const exchanges = useMemo(() => (messages.data ? pairMessages(messages.data) : undefined), [messages.data]);

  return (
    <DataTable
      label="OCPP messages"
      rows={exchanges}
      loading={messages.isPending}
      error={messages.error}
      searchText={(ex) => `${ex.action} ${ex.request ? JSON.stringify(ex.request.payload) : ''} ${ex.reply ? JSON.stringify(ex.reply.payload) : ''} ${ex.reply?.errorCode ?? ''}`}
      searchPlaceholder="Search the messages"
      toolbar={
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel control={<Switch checked={hideHeartbeats} onChange={(e) => setHide(e.target.checked)} />} label="Hide heartbeats" />
          <Button size="small" startIcon={<RefreshCw size={16} strokeWidth={1.75} />} onClick={() => void messages.refetch()}>Refresh</Button>
        </Box>
      }
      columns={[
        { key: 'when', header: 'Time', width: 150, render: (ex) => <span className="num" style={{ whiteSpace: 'nowrap' }}>{time.format(new Date(ex.at))}</span> },
        {
          key: 'msg',
          header: 'Request and reply',
          render: (ex: Exchange) => {
            const o = outcome(ex);
            const expanded = open === ex.id;
            const fromCharger = ex.direction === 'in';
            return (
              <Box sx={{ minWidth: 0, width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap', mb: 0.75 }}>
                  <Typography sx={{ fontWeight: 600 }}>{ex.action}</Typography>
                  {o && (
                    <Typography variant="body2" sx={{ fontWeight: 500, color: o.tone === 'ok' ? colours.successGreen : o.tone === 'bad' ? colours.faultRed : colours.graphite }}>
                      {o.text}
                      {replyTime(ex) && <Box component="span" sx={{ color: colours.graphite, fontWeight: 400 }}> · {replyTime(ex)}</Box>}
                    </Typography>
                  )}
                  {/* Which way the request went, at the right-hand end */}
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    aria-label={fromCharger ? 'Charger to server' : 'Server to charger'}
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'center', ml: 'auto', whiteSpace: 'nowrap' }}
                  >
                    Server {fromCharger ? <MoveLeft size={18} strokeWidth={1.5} aria-hidden /> : <MoveRight size={18} strokeWidth={1.5} aria-hidden />} Charger
                  </Typography>
                </Box>
                {/* The two halves of the exchange, joined by a line on the left. */}
                <Box
                  onClick={() => setOpen(expanded ? null : ex.id)}
                  title={expanded ? 'Show less' : 'Show all'}
                  sx={{ borderLeft: `2px solid ${colours.hairline}`, pl: 1.5, display: 'grid', gap: 0.5, cursor: 'pointer', maxWidth: { xs: '72vw', sm: 680 } }}
                >
                  <Line icon={fromCharger ? 'in' : 'out'} label="Request" text={ex.request ? body(ex.request, expanded) : 'Older than what is shown'} muted={!ex.request} expanded={expanded} />
                  <Line
                    icon={fromCharger ? 'out' : 'in'}
                    label="Reply"
                    text={ex.reply ? body(ex.reply, expanded) : '—'}
                    muted={!ex.reply}
                    error={ex.reply?.messageTypeId === 4}
                    expanded={expanded}
                  />
                </Box>
              </Box>
            );
          },
        },
      ]}
      rowKey={(ex) => ex.id}
      pageSize={50}
      empty={<EmptyState title="No messages yet">Messages appear once the charger connects.</EmptyState>}
    />
  );
}

/** One half of an exchange: an arrow for which way it went, a label, and the JSON. */
function Line({ icon, label, text, muted, error, expanded }: { icon: 'in' | 'out'; label: string; text: string; muted?: boolean; error?: boolean; expanded: boolean }) {
  const Arrow = icon === 'in' ? ArrowDownLeft : ArrowUpRight;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '18px 64px minmax(0, 1fr)', alignItems: 'start', columnGap: 0.5 }}>
      <Box component="span" aria-label={icon === 'in' ? 'from the charger' : 'to the charger'} sx={{ color: colours.graphite, display: 'flex', pt: '2px' }}>
        <Arrow size={15} strokeWidth={1.5} />
      </Box>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      <Box
        component="code"
        sx={{
          fontFamily: mono,
          fontSize: 13,
          color: error ? colours.faultRed : muted ? colours.coolGrey : 'text.secondary',
          minWidth: 0,
          ...(expanded ? { whiteSpace: 'pre-wrap', wordBreak: 'break-all' } : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
        }}
      >
        {text}
      </Box>
    </Box>
  );
}
